import { useCallback } from 'react';
import type { FC } from 'react';

import type { SvpAssetAdditional } from '@vgtv/api-client/lib/svp_asset';
import type { Provider } from '@vgtv/api-client';

import { useOverlay } from '../Overlay/useOverlay';
import type { ButtonProps } from '../Button/Button';
import { Button } from '../Button/Button';
import { trackButtonClickEvent } from '../../services/tracking/pulseEvents';
import type { ProviderSubscriptionMap } from '../../services/tracking/pulseEvents';
import type { Section } from '../../services/sps/salesposter';

import { PaywallPopup } from './PaywallPopup/PaywallPopup';

interface Props {
  asset: SvpAssetAdditional;
  access: string;
  buttonLabel: string;
  buttonSize?: ButtonProps['size'];
  onLoadFailure: (thrownValue: unknown) => void;
}

const getSection = (access: string): Section => {
  switch (access) {
    case 'sport':
      return 'sport';
    default:
      return 'default';
  }
};

export const PaywallButton: FC<Props> = ({
  asset,
  access,
  onLoadFailure,
  buttonLabel,
  buttonSize = 'large',
}) => {
  const { isOpen, open, ariaAttributes, close } = useOverlay();

  const onOpenClick = useCallback(() => {
    const button = {
      elementType: 'PaywallButton',
      title: buttonLabel,
    };

    const target = {
      elementType: 'SubscriptionPaywall',
      name: 'SubscriptionPaywall',
      url: window.location.href,
    };

    trackButtonClickEvent(button, target).then(open).catch(open);

    open();
  }, [open, buttonLabel]);

  return (
    <>
      <Button
        size={buttonSize}
        variant="contained"
        rounded
        isActiveBrandColor
        onClick={onOpenClick}
      >
        {buttonLabel}
      </Button>
      <PaywallPopup
        asset={asset}
        isOpen={isOpen}
        onCloseButtonClick={close}
        {...ariaAttributes}
        access={access as ProviderSubscriptionMap[Provider.VGTV]}
        section={getSection(access)}
        onLoadFailure={onLoadFailure}
      />
    </>
  );
};
